(() => {
  if (window.DZProjectSectionsLoaded) return;
  window.DZProjectSectionsLoaded = true;

  const onReady = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const isShopifyDesignMode = () =>
    Boolean(window.Shopify && window.Shopify.designMode);

  const initSlider = (root) => {
    if (!root || root.dataset.dzInitialized === 'true') return;

    const track = root.querySelector('[data-dz-slider-track]');
    const scrollbar = root.querySelector('[data-dz-scrollbar]');
    const rail = root.querySelector('[data-dz-scrollbar-rail]');
    const thumb = root.querySelector('[data-dz-scrollbar-thumb]');
    const previous = root.querySelector('[data-dz-slider-prev]');
    const next = root.querySelector('[data-dz-slider-next]');

    if (!track) return;
    root.dataset.dzInitialized = 'true';

    const getMaxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);

    const getCardDistance = () => {
      const firstCard = track.firstElementChild;
      return firstCard
        ? firstCard.getBoundingClientRect().width + 14
        : track.clientWidth * 0.82;
    };

    const updateLegacyButtons = () => {
      if (!previous || !next) return;
      const maxScroll = getMaxScroll();
      const current = Math.max(0, track.scrollLeft);
      previous.disabled = current <= 4;
      next.disabled = current >= maxScroll - 4;
    };

    const updateScrollbar = () => {
      if (!scrollbar || !rail || !thumb) {
        updateLegacyButtons();
        return;
      }

      const maxScroll = getMaxScroll();
      const railWidth = rail.clientWidth;

      if (maxScroll <= 2 || railWidth <= 0) {
        scrollbar.hidden = true;
        thumb.setAttribute('aria-valuenow', '0');
        return;
      }

      scrollbar.hidden = false;

      const visibleRatio = Math.min(1, track.clientWidth / track.scrollWidth);
      const thumbWidth = Math.max(48, railWidth * visibleRatio);
      const maxThumbX = Math.max(0, railWidth - thumbWidth);
      const progress = Math.min(1, Math.max(0, track.scrollLeft / maxScroll));
      const thumbX = progress * maxThumbX;

      thumb.style.width = `${thumbWidth}px`;
      thumb.style.transform = `translate3d(${thumbX}px, 0, 0)`;
      thumb.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
      updateLegacyButtons();
    };

    const scrollByCard = (direction) => {
      track.scrollBy({
        left: direction * getCardDistance(),
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    };

    if (previous) previous.addEventListener('click', () => scrollByCard(-1));
    if (next) next.addEventListener('click', () => scrollByCard(1));

    if (scrollbar && rail && thumb) {
      let dragging = false;
      let startPointerX = 0;
      let startScrollLeft = 0;

      const moveFromPointer = (clientX) => {
        const maxScroll = getMaxScroll();
        const railWidth = rail.clientWidth;
        const thumbWidth = thumb.getBoundingClientRect().width;
        const maxThumbX = Math.max(1, railWidth - thumbWidth);
        const deltaX = clientX - startPointerX;
        track.scrollLeft = startScrollLeft + (deltaX / maxThumbX) * maxScroll;
      };

      thumb.addEventListener('pointerdown', (event) => {
        dragging = true;
        startPointerX = event.clientX;
        startScrollLeft = track.scrollLeft;
        thumb.setPointerCapture(event.pointerId);
        event.preventDefault();
      });

      thumb.addEventListener('pointermove', (event) => {
        if (!dragging) return;
        moveFromPointer(event.clientX);
      });

      const stopDragging = (event) => {
        if (!dragging) return;
        dragging = false;
        if (thumb.hasPointerCapture(event.pointerId)) {
          thumb.releasePointerCapture(event.pointerId);
        }
      };

      thumb.addEventListener('pointerup', stopDragging);
      thumb.addEventListener('pointercancel', stopDragging);

      rail.addEventListener('pointerdown', (event) => {
        if (event.target === thumb) return;
        const rect = rail.getBoundingClientRect();
        const maxScroll = getMaxScroll();
        const percentage = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
        track.scrollTo({
          left: maxScroll * percentage,
          behavior: prefersReducedMotion() ? 'auto' : 'smooth'
        });
      });

      thumb.addEventListener('keydown', (event) => {
        const maxScroll = getMaxScroll();
        let nextScroll = track.scrollLeft;

        if (event.key === 'ArrowLeft') nextScroll -= getCardDistance() * 0.5;
        else if (event.key === 'ArrowRight') nextScroll += getCardDistance() * 0.5;
        else if (event.key === 'PageUp') nextScroll -= track.clientWidth * 0.8;
        else if (event.key === 'PageDown') nextScroll += track.clientWidth * 0.8;
        else if (event.key === 'Home') nextScroll = 0;
        else if (event.key === 'End') nextScroll = maxScroll;
        else return;

        event.preventDefault();
        track.scrollTo({
          left: Math.max(0, Math.min(maxScroll, nextScroll)),
          behavior: prefersReducedMotion() ? 'auto' : 'smooth'
        });
      });
    }

    track.addEventListener('scroll', updateScrollbar, { passive: true });
    window.addEventListener('resize', updateScrollbar, { passive: true });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(updateScrollbar);
      resizeObserver.observe(track);
      resizeObserver.observe(root);
    }

    track.querySelectorAll('img').forEach((image) => {
      if (!image.complete) image.addEventListener('load', updateScrollbar, { once: true });
    });

    requestAnimationFrame(updateScrollbar);
  };

  /**
   * Keeps the active anchor visible inside the horizontal nav only.
   * Never calls link.scrollIntoView(), because that can move the whole page
   * vertically back to the hero/nav position when IntersectionObserver fires.
   */
  const centerAnchorLinkHorizontally = (nav, link) => {
    if (!nav || !link || nav.scrollWidth <= nav.clientWidth + 2) return;

    const left = link.offsetLeft - (nav.clientWidth - link.offsetWidth) / 2;
    nav.scrollTo({
      left: Math.max(0, left),
      behavior: prefersReducedMotion() ? 'auto' : 'smooth'
    });
  };

  const initAnchorNav = (nav) => {
    if (!nav || nav.dataset.dzInitialized === 'true') return;
    nav.dataset.dzInitialized = 'true';

    const links = [...nav.querySelectorAll('[data-dz-anchor-link]')];
    const navWrap = nav.closest('.dzp-anchor-nav-wrap');

    const targets = links
      .map((link) => {
        const selector = link.getAttribute('href');
        if (!selector || !selector.startsWith('#')) return null;

        try {
          return { link, selector, target: document.querySelector(selector) };
        } catch (_) {
          return null;
        }
      })
      .filter((item) => item && item.target);

    if (!targets.length) return;

    let activeLink = null;

    const setActiveLink = (link) => {
      if (!link || activeLink === link) return;
      activeLink = link;

      links.forEach((item) => {
        const isActive = item === link;
        item.classList.toggle('is-active', isActive);

        if (isActive) {
          item.setAttribute('aria-current', 'location');
        } else {
          item.removeAttribute('aria-current');
        }
      });

      centerAnchorLinkHorizontally(nav, link);
    };

    const getScrollOffset = () => {
      if (!navWrap) return 12;

      const styles = window.getComputedStyle(navWrap);
      const configuredTop = Number.parseFloat(
        styles.getPropertyValue('--dzp-sticky-top') || '0'
      );
      const navHeight = navWrap.classList.contains('is-sticky')
        ? navWrap.getBoundingClientRect().height
        : 0;

      return Math.max(0, configuredTop) + navHeight + 12;
    };

    targets.forEach(({ link, selector, target }) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        setActiveLink(link);

        const top =
          window.scrollY + target.getBoundingClientRect().top - getScrollOffset();

        window.scrollTo({
          top: Math.max(0, top),
          behavior: prefersReducedMotion() ? 'auto' : 'smooth'
        });

        // Do not alter the Theme Editor URL/history while merchants configure sections.
        if (!isShopifyDesignMode()) {
          window.history.replaceState(null, '', selector);
        }
      });
    });

    const hashMatch = targets.find(({ selector }) => selector === window.location.hash);
    setActiveLink(hashMatch ? hashMatch.link : targets[0].link);

    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => {
            const ratioDifference = b.intersectionRatio - a.intersectionRatio;
            if (Math.abs(ratioDifference) > 0.001) return ratioDifference;
            return Math.abs(a.boundingClientRect.top) - Math.abs(b.boundingClientRect.top);
          })[0];

        if (!visible) return;
        const match = targets.find(({ target }) => target === visible.target);
        if (match) setActiveLink(match.link);
      },
      {
        root: null,
        rootMargin: '-28% 0px -58% 0px',
        threshold: [0.01, 0.15, 0.35]
      }
    );

    targets.forEach(({ target }) => observer.observe(target));
  };

  const validateCheckboxGroup = (group) => {
    const checkboxes = [...group.querySelectorAll('input[type="checkbox"]')];
    const hiddenInput = group.querySelector('[data-dz-group-value]');
    const error = group.querySelector('[data-dz-group-error]');
    const required = group.dataset.required === 'true';
    const max = Number.parseInt(group.dataset.maxSelect || '0', 10);
    const checked = checkboxes.filter((checkbox) => checkbox.checked);

    if (max > 0 && checked.length > max) {
      const lastChecked = checked[checked.length - 1];
      if (lastChecked) lastChecked.checked = false;
    }

    const finalChecked = checkboxes.filter((checkbox) => checkbox.checked);
    if (hiddenInput) {
      hiddenInput.value = finalChecked.map((checkbox) => checkbox.value).join(', ');
    }

    const isValid = !required || finalChecked.length > 0;
    if (error) error.classList.toggle('is-visible', !isValid);
    return isValid;
  };

  const initProjectForm = (form) => {
    if (!form || form.dataset.dzInitialized === 'true') return;
    form.dataset.dzInitialized = 'true';

    const groups = [...form.querySelectorAll('[data-dz-check-group]')];
    const submitButton = form.querySelector('[type="submit"]');
    const idleLabel = submitButton?.textContent || 'Submit';
    const sendingLabel = submitButton?.dataset.sendingLabel || 'Sending…';

    groups.forEach((group) => {
      group.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
        checkbox.addEventListener('change', () => validateCheckboxGroup(group));
      });
      validateCheckboxGroup(group);
    });

    form.addEventListener('submit', (event) => {
      const groupsValid = groups.every((group) => validateCheckboxGroup(group));
      const formValid = form.checkValidity();

      if (!groupsValid || !formValid) {
        event.preventDefault();
        form.reportValidity();
        const firstError = form.querySelector('.dzp-group-error.is-visible, :invalid');
        if (firstError) {
          firstError.scrollIntoView({
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
            block: 'center'
          });
        }
        return;
      }

      if (submitButton) {
        submitButton.disabled = true;
        submitButton.setAttribute('aria-busy', 'true');
        submitButton.textContent = sendingLabel;

        window.setTimeout(() => {
          submitButton.disabled = false;
          submitButton.removeAttribute('aria-busy');
          submitButton.textContent = idleLabel;
        }, 12000);
      }
    });
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-dz-slider]').forEach(initSlider);
    scope.querySelectorAll('[data-dz-anchor-nav]').forEach(initAnchorNav);
    scope.querySelectorAll('[data-dz-project-form]').forEach(initProjectForm);
  };

  onReady(() => initAll());

  // Theme Editor section reloads are supported, but block selection no longer
  // forces any vertical page scrolling. This prevents editor-only jump loops.
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();